import { Patient } from "@/services/patient-service";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Edit2,
  Trash2,
  CheckCircle,
  XCircle,
  Calendar,
  FileText,
  Send,
} from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatCPF, formatPhone, formatDateBR } from "@/utils/formatters";

interface PatientsTableProps {
  patients: Patient[];
  loading?: boolean;
  onEdit: (patient: Patient) => void;
  onDelete: (patient: Patient) => void;
  onActivate: (patient: Patient) => void;
  onInactivate: (patient: Patient) => void;
  onInviteSchedule: (patient: Patient) => void;
  onViewRecord: (patient: Patient) => void;
  onSendLink: (patient: Patient) => void;
}

export function PatientsTable({ 
  patients, 
  loading = false,
  onEdit, 
  onDelete, 
  onActivate, 
  onInactivate,
  onInviteSchedule,
  onViewRecord,
  onSendLink,
}: PatientsTableProps) {
  return (
    <div className="rounded-lg border border-border overflow-hidden bg-card">
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/50 hover:bg-muted/50">
              <TableHead className="th">Nome</TableHead>
              <TableHead className="th">CPF</TableHead>
              <TableHead className="th">Data Nasc.</TableHead>
              <TableHead className="th">Email</TableHead>
              <TableHead className="th">Telefone</TableHead>
              <TableHead className="th">Cidade</TableHead>
              <TableHead className="th">UF</TableHead>
              <TableHead className="th">Status</TableHead>
              <TableHead className="th text-center">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              Array.from({ length: 5 }).map((_, index) => (
                <TableRow key={index}>
                  <TableCell colSpan={9}><Skeleton className="h-10 w-full" /></TableCell>
                </TableRow>
              ))
            ) : patients.map((patient) => (
              <TableRow key={patient.id} className="hover:bg-muted/30 transition-colors">
                <TableCell className="py-1.5 px-2 text-xs max-w-[200px] font-medium">{patient.name}</TableCell>
                <TableCell className="td">{formatCPF(patient.cpf)}</TableCell>
                <TableCell className="td">{formatDateBR(patient.birth_date)}</TableCell>
                <TableCell className="py-1.5 px-2 text-xs max-w-[200px] truncate">{patient.email}</TableCell>
                <TableCell className="td">{formatPhone(patient.cellphone)}</TableCell>
                <TableCell className="td">{patient.city_name}</TableCell>
                <TableCell className="td">{patient.state_code}</TableCell>
                <TableCell className="td">
                  <Badge variant={patient.status === 1 ? "default" : "secondary"} className="text-xs py-0">
                    {patient.status === 1 ? "Ativo" : "Inativo"}
                  </Badge>
                </TableCell>
                <TableCell className="td text-center">
                  <div className="flex items-center justify-center gap-1">

                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 w-7 p-0 hover:bg-blue-500/10 hover:text-blue-600"
                      onClick={() => onInviteSchedule(patient)} // Esta função agora abrirá o Modal
                      title="Realizar Agendamento"
                    >
                      <Calendar className="h-3.5 w-3.5" />
                    </Button>
                    
                    {/* Botão Ver Prontuário */}
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 w-7 p-0 hover:bg-slate-500/10 hover:text-slate-600"
                      onClick={() => onViewRecord(patient)}
                      title="Ver Prontuário"
                    >
                      <FileText className="h-3.5 w-3.5" />
                    </Button>

                    {/* Botão Convite de Agendamento */}
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 w-7 p-0 hover:bg-teal-500/10 hover:text-teal-600"
                      onClick={() => onSendLink(patient)}
                      title="Convite de Agendamento"
                    >
                      <Send className="h-3.5 w-3.5" />
                    </Button>

                    <div className="w-px h-4 bg-border mx-0.5" />

                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 w-7 p-0 hover:bg-primary/10 hover:text-primary"
                      onClick={() => onEdit(patient)}
                      title="Editar"
                    >
                      <Edit2 className="h-3.5 w-3.5" />
                    </Button>

                    {patient.status === 1 ? (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 w-7 p-0 hover:bg-orange-500/10 hover:text-orange-500"
                        onClick={() => onInactivate(patient)}
                        title="Inativar"
                      >
                        <XCircle className="h-3.5 w-3.5" />
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 w-7 p-0 hover:bg-green-500/10 hover:text-green-500"
                        onClick={() => onActivate(patient)}
                        title="Ativar"
                      >
                        <CheckCircle className="h-3.5 w-3.5" />
                      </Button>
                    )}

                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 w-7 p-0 hover:bg-destructive/10 hover:text-destructive"
                      onClick={() => onDelete(patient)}
                      title="Deletar"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}